import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAppStore } from "../store/useAppStore";
import { scanService } from "../services/scanService";
import type { UniversalScanResult } from "../services/scanService";
import { maxPlatform } from "../services/maxPlatform";
import { eventsService } from "../services/eventsService";
import { ApiError } from "../api/client";
import { formatScanApiError } from "../utils/scanErrors";
import { PageHeader } from "../components/PageHeader";
import { ScanResultModal } from "../components/ScanResultModal";
import { CodeScanner, type CodeScanResult } from "../components/CodeScanner";

type ScanState = "idle" | "scanning" | "success" | "error";

/**
 * Unified scan UX for browser, MAX mini-app, and future native shells.
 * Fills the locked RootShell viewport (see lockViewport for /scan) so the
 * camera flex area does not collapse to a black zero-height box.
 */
export function ScanPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedEventId, selectEvent, addScan, saveContact, user } = useAppStore();

  const [scanState, setScanState] = useState<ScanState>("idle");
  const [scanResult, setScanResult] = useState<UniversalScanResult["scanResult"] | null>(null);
  const [scanLeadId, setScanLeadId] = useState<string | undefined>();
  const [scanId, setScanId] = useState<string | undefined>();
  const [scanNotes, setScanNotes] = useState("");
  const [scanTags, setScanTags] = useState<string[]>([]);
  const [alreadyScanned, setAlreadyScanned] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  /** IDEA-048: flip false before leave so CodeScanner stops tracks immediately. */
  const [cameraActive, setCameraActive] = useState(true);
  const processingRef = useRef(false);

  useEffect(() => {
    setCameraActive(true);
    return () => {
      setCameraActive(false);
    };
  }, []);

  const processScan = useCallback(
    async (code: string, codeType: "qr" | "barcode") => {
      const trimmed = code.trim();
      if (!trimmed || processingRef.current) return;

      processingRef.current = true;
      setScanState("scanning");
      setLastError(null);
      maxPlatform.hapticFeedback("medium");

      try {
        let result = await scanService
          .scanCode(trimmed, {
            eventId: selectedEventId ?? undefined,
            userId: user.id,
            codeType,
          })
          .catch((err) => {
            if (err instanceof ApiError && err.status === 404) return null;
            throw err;
          });

        if (!result && selectedEventId) {
          const all = await eventsService.getAll().catch(() => []);
          const current = all.find((e) => e.id === selectedEventId);
          const siblings = current?.groupId
            ? all.filter((e) => e.groupId === current.groupId && e.id !== selectedEventId)
            : [];
          for (const sib of siblings) {
            result = await scanService
              .scanCode(trimmed, {
                eventId: sib.id,
                userId: user.id,
                codeType,
              })
              .catch((err) => {
                if (err instanceof ApiError && err.status === 404) return null;
                throw err;
              });
            if (result) {
              selectEvent(sib.id);
              toast.message(
                t("scan.switched_event", { title: sib.title || sib.id }),
              );
              break;
            }
          }
        }

        if (!result) {
          setScanState("error");
          setLastError(t("scan.unrecognized"));
          maxPlatform.hapticFeedback("error");
          processingRef.current = false;
          setTimeout(() => setScanState("idle"), 2000);
          return;
        }

        if (!result.alreadyScanned) {
          addScan(result.scanLog);
          if (result.savedContact) {
            saveContact(result.savedContact);
          }
        } else {
          // Keep local «Мои сканирования» in sync when API says duplicate.
          addScan(result.scanLog);
        }

        if (result.scanLog.eventId && result.scanLog.eventId !== selectedEventId) {
          selectEvent(result.scanLog.eventId);
        }

        setScanResult(result.scanResult);
        setScanLeadId(result.leadId);
        setScanId(result.scanLog.id);
        setScanNotes(result.scanLog.notes ?? "");
        setScanTags(result.scanLog.tags ?? []);
        setAlreadyScanned(result.alreadyScanned);
        setScanState("success");
        maxPlatform.hapticFeedback("success");
      } catch (err) {
        const message = formatScanApiError(err);
        setScanState("error");
        setLastError(message);
        maxPlatform.hapticFeedback("error");
        toast.error(message);
        processingRef.current = false;
        setTimeout(() => setScanState("idle"), 2500);
      }
    },
    [addScan, saveContact, selectEvent, selectedEventId, t, user.id],
  );

  const handleCameraScan = useCallback(
    (result: CodeScanResult) => {
      void processScan(result.text, result.codeType);
    },
    [processScan],
  );

  const handleCloseResult = () => {
    processingRef.current = false;
    setScanState("idle");
    setScanResult(null);
    setScanLeadId(undefined);
    setScanId(undefined);
    setScanNotes("");
    setScanTags([]);
    setLastError(null);
  };

  const cameraPaused = scanState === "scanning" || scanState === "success";

  return (
    <>
      <div className="flex flex-col flex-1 min-h-0 h-full w-full overflow-hidden">
      <PageHeader
        title={t("scan.title")}
        back
        className="shrink-0"
        onBack={() => {
          // IDEA-048: stop camera tracks before route change (MAX keeps LED otherwise).
          setCameraActive(false);
          const idx = (window.history.state as { idx?: number } | null)?.idx;
          if (typeof idx === "number" && idx > 0) {
            navigate(-1);
            return;
          }
          navigate("/home");
        }}
      />
        <div className="flex-1 min-h-0 px-3 pt-1 pb-[max(5.5rem,calc(4.5rem+env(safe-area-inset-bottom)))] flex flex-col gap-2">
          {/* min-h fallback: without a definite height, absolute video paints a black void */}
          <div className="relative flex-1 min-h-[min(52vh,420px)] rounded-2xl overflow-hidden border border-gray-100 bg-black">
            <CodeScanner
              userId={user.id}
              active={cameraActive}
              variant="scan"
              paused={cameraPaused}
              onScan={handleCameraScan}
              className="absolute inset-0 h-full w-full min-h-full rounded-none"
            />
          </div>
          {(scanState === "scanning" || (scanState === "error" && lastError)) && (
            <div className="shrink-0 text-center min-h-[28px]">
              {scanState === "scanning" && (
                <p className="text-[13px] text-indigo-600 font-medium flex items-center justify-center gap-2">
                  <Loader2 size={14} className="animate-spin" />
                  {t("scan.processing")}
                </p>
              )}
              {scanState === "error" && lastError && (
                <p className="text-[13px] text-red-500 flex items-center justify-center gap-1.5">
                  <AlertCircle size={16} />
                  {lastError}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {scanState === "success" && scanResult && (
        <ScanResultModal
          scanResult={scanResult}
          alreadyScanned={alreadyScanned}
          leadId={scanLeadId}
          scanId={scanId}
          initialNotes={scanNotes}
          initialTags={scanTags}
          eventId={selectedEventId ?? undefined}
          onClose={handleCloseResult}
        />
      )}
    </>
  );
}
